import React, { useCallback, useRef, useState } from "react";
import { logix2Dock } from "@/api/adminOneClient";

/**
 * CareLogix™ ORACLE — LOGIX-2™ Image Search Button (Phase 20)
 *
 * Hidden file input. User picks a chart/screenshot. We:
 *   1) Preview the image
 *   2) POST /api/logix2/image-search
 *   3) Hand the structured analysis to `onResult`
 *
 * Props:
 *   - onResult({ analysis, image_data_url, prompt })
 *   - prompt: optional additional instruction
 *   - className
 */

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB client-side cap

function blobToDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

export default function Logix2ImageSearchButton({ onResult, prompt = "", className = "", title = "Image search" } = {}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = () => {
    setError("");
    try { fileRef.current && fileRef.current.click(); } catch { /* ignore */ }
  };

  const onChange = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(`Image too large (max ${Math.round(MAX_BYTES / 1024 / 1024)} MB).`);
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await blobToDataURL(file);
      const fd = new FormData();
      fd.append("image", file);
      if (prompt) fd.append("prompt", prompt);
      const { data } = await logix2Dock.imageSearch(fd);
      onResult && onResult({ analysis: data, image_data_url: dataUrl, prompt });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "image_search_failed");
      onResult && onResult({ error: err?.message || "image_search_failed" });
    } finally {
      setBusy(false);
      try { e.target.value = ""; } catch { /* ignore */ }
    }
  }, [onResult, prompt]);

  return (
    <>
      <button
        type="button"
        className={`l2-icon-btn l2-icon-btn--img ${className}`}
        onClick={pick}
        disabled={busy}
        aria-label="Upload image for LOGIX-2 to analyse"
        title={title}
        data-testid="logix2-image-button"
      >
        {busy ? "…" : "📷"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
        onChange={onChange}
        style={{ display: "none" }}
        data-testid="logix2-image-input"
      />
      {error && <span className="l2-icon-btn__error" role="alert">{error}</span>}
    </>
  );
}
